import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { IoIosLogIn, IoMdPricetags } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { GetCompanies, type Company } from '../Services/CompanyServices';
import { useAuth } from '../utils/AuthContext';

type LoginCredentials = { email: string; password: string; companyId: string };

function Login() {
    const { user, rememberedEmail, handleUserLogin } = useAuth();
    const navigate = useNavigate();
    const [credentials, setCredentials] = useState<LoginCredentials>({ email: '', password: '', companyId: '' });
    const [remember, setRemember] = useState(false);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [companiesLoading, setCompaniesLoading] = useState(true);
    const [companiesError, setCompaniesError] = useState('');

    useEffect(() => {
        let isMounted = true;

        GetCompanies()
            .then(result => {
                if (isMounted) setCompanies(result);
            })
            .catch(error => {
                if (isMounted) {
                    setCompaniesError(error instanceof Error ? error.message : 'Firma listesi Appwrite üzerinden alınamadı.');
                }
            })
            .finally(() => {
                if (isMounted) setCompaniesLoading(false);
            });

        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (rememberedEmail) {
            setCredentials(current => ({ ...current, email: rememberedEmail }));
            setRemember(true);
        }
    }, [rememberedEmail]);

    useEffect(() => {
        if (user) navigate('/home', { replace: true });
    }, [navigate, user]);

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.currentTarget;
        setCredentials(current => ({ ...current, [name]: value }));
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (companiesLoading || companies.length === 0) {
            event.preventDefault();
            return;
        }
        void handleUserLogin(event, credentials, remember);
    };

    return (
        <main className="login-screen relative isolate flex items-center justify-center overflow-x-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4 py-12 text-slate-100 sm:px-6">
            <div aria-hidden="true" className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
            <div aria-hidden="true" className="pointer-events-none absolute -bottom-48 -right-32 h-[30rem] w-[30rem] rounded-full bg-blue-500/10 blur-3xl" />

            <section aria-labelledby="login-heading" className="relative w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/90 p-6 shadow-2xl shadow-black/30 backdrop-blur sm:p-9">
                <div className="mb-8 flex items-center gap-4">
                    <img src="/portal_icon.png" alt="Portal" className="h-14 w-14 shrink-0 rounded-2xl" />
                    <div>
                        <p className="text-sm font-medium uppercase tracking-[0.18em] text-cyan-300">Portal <span className="text-slate-400">v4.0</span></p>
                        <h1 id="login-heading" className="mt-1 text-xl font-semibold tracking-tight text-white sm:text-2xl">Fiyat Onay Programı</h1>
                    </div>
                </div>

                <div className="mb-6">
                    <h2 className="text-lg font-semibold text-white">Hesabınıza giriş yapın</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-400">Devam etmek için hesap bilgilerinizi ve firmanızı seçin.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-200">Email / Kullanıcı Adı</label>
                        <input
                            type="text"
                            id="email"
                            name="email"
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck={false}
                            className="h-12 w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 text-base text-white outline-none transition placeholder:text-slate-500 hover:border-slate-600 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/15"
                            placeholder="Kullanıcı adınızı girin"
                            value={credentials.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-200">Şifre</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            autoComplete="current-password"
                            className="h-12 w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 text-base text-white outline-none transition placeholder:text-slate-500 hover:border-slate-600 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/15"
                            placeholder="Şifrenizi girin"
                            value={credentials.password}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="companyId" className="mb-2 block text-sm font-medium text-slate-200">Firma</label>
                        <select
                            id="companyId"
                            name="companyId"
                            autoComplete="organization"
                            className="h-12 w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 text-base text-white outline-none transition hover:border-slate-600 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/15"
                            value={credentials.companyId}
                            onChange={handleChange}
                            required
                        >
                            <option value="" disabled>
                                {companiesLoading ? 'Firmalar yükleniyor…' : companiesError ? 'Firma listesi alınamadı' : 'Firma seçin'}
                            </option>
                            {companies.map(company => (
                                <option key={company.companyId} value={company.companyId}>{company.companyName}</option>
                            ))}
                        </select>
                        {companiesLoading && <p role="status" className="mt-2 text-sm text-slate-400">Firma listesi Appwrite’dan yükleniyor…</p>}
                        {companiesError && <p role="alert" className="mt-2 text-sm leading-5 text-rose-300">{companiesError}</p>}
                    </div>

                    <label htmlFor="remember" className="flex min-h-8 w-fit cursor-pointer items-center gap-3 text-sm text-slate-300">
                        <input
                            type="checkbox"
                            id="remember"
                            name="remember"
                            className="h-4 w-4 rounded border-slate-600 bg-slate-950 text-cyan-400 accent-cyan-400 focus:ring-2 focus:ring-cyan-400/40"
                            checked={remember}
                            onChange={event => setRemember(event.currentTarget.checked)}
                        />
                        Email adresimi hatırla
                    </label>

                    <button type="submit" disabled={companiesLoading || companies.length === 0} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-cyan-400 px-4 text-base font-semibold text-slate-950 shadow-lg shadow-cyan-950/20 transition hover:bg-cyan-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/40 disabled:cursor-not-allowed disabled:opacity-50">
                        <IoIosLogIn aria-hidden="true" size={20} />
                        Giriş Yap
                    </button>
                </form>

                <p className="mt-6 flex items-center justify-center gap-2 border-t border-slate-800 pt-5 text-xs text-slate-400">
                    <IoMdPricetags aria-hidden="true" size={16} className="text-cyan-300" />
                    Proforma fiyat değişiklikleri için güvenli oturum açma
                </p>
            </section>
        </main>
    );
}

export default Login;
